import React, { CSSProperties } from 'react'
import { Checkbox, Divider, Position } from '@blueprintjs/core'
import { Popover } from '@ui'
import {
  EmployeeSelect,
  DepartmentSelect,
  LocationSelect,
  EmployeeTypeSelect,
  RoleSelect,
  Flex,
  IconButton,
} from '@ui'
import { ValueFilter } from '@api/employee/types'
import { get, without } from 'lodash'
import track, { RootEvent } from '@track'
import Event from '@track/Event'
import { EmployeeCatalogFiltersT } from './hooks/useEmployeeCatalog'

interface FiltersProps {
  filterCount: number
  filters: EmployeeCatalogFiltersT
  applyFilters: (filters: EmployeeCatalogFiltersT) => void
  style?: CSSProperties
  targetStyle?: CSSProperties
}

interface FilterIconButtonProps {
  filterCount: number
  onClick?: () => void
  style?: CSSProperties
}

function FilterIconButton({ onClick, style }: FilterIconButtonProps) {
  return (
    <Flex style={{ position: 'relative' }}>
      <IconButton minimal iconName="filter" onClick={onClick} style={{ ...style }} />
    </Flex>
  )
}

export default function FiltersComponent({
  filterCount,
  filters,
  applyFilters,
  style,
  targetStyle,
}: FiltersProps) {
  return (
    <Popover
      minimal
      captureDismiss
      position={Position.BOTTOM_RIGHT}
      canEscapeKeyClose={false}
      content={
        <div style={{ minWidth: 200, padding: 10, ...style }} onClick={(e) => e.stopPropagation()}>
          <Flex>
            <table style={{ marginRight: 24, marginTop: 7 }}>
              <tbody>
                <tr>
                  <td style={{ height: 30 }}>
                    <div style={{ padding: 'auto 0' }}>
                      <Checkbox
                        checked={filters.valueFilter.indexOf(ValueFilter.SEATED) >= 0}
                        onChange={() => {
                          applyFilters({
                            ...filters,
                            valueFilter:
                              filters.valueFilter.indexOf(ValueFilter.SEATED) >= 0
                                ? without(filters.valueFilter, ValueFilter.SEATED)
                                : [...filters.valueFilter, ValueFilter.SEATED],
                          })

                          const payload: RootEvent =
                            filters.valueFilter.indexOf(ValueFilter.SEATED) >= 0
                              ? {
                                  eventName: Event.CLEAR_FILTER,
                                  props: {
                                    filterName: 'Seated',
                                    filterValue: null,
                                  },
                                }
                              : {
                                  eventName: Event.FILTER,
                                  props: {
                                    filterName: 'Seated',
                                    filterValue: true,
                                  },
                                }

                          track.employees(payload)
                        }}
                      >
                        Seated
                      </Checkbox>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style={{ height: 30 }}>
                    <div style={{ padding: 'auto 0' }}>
                      <Checkbox
                        checked={filters.valueFilter.indexOf(ValueFilter.UNSEATED) >= 0}
                        onChange={() => {
                          applyFilters({
                            ...filters,
                            valueFilter:
                              filters.valueFilter.indexOf(ValueFilter.UNSEATED) >= 0
                                ? without(filters.valueFilter, ValueFilter.UNSEATED)
                                : [...filters.valueFilter, ValueFilter.UNSEATED],
                          })

                          const payload: RootEvent =
                            filters.valueFilter.indexOf(ValueFilter.UNSEATED) >= 0
                              ? {
                                  eventName: Event.CLEAR_FILTER,
                                  props: {
                                    filterName: 'Unseated',
                                    filterValue: null,
                                  },
                                }
                              : {
                                  eventName: Event.FILTER,
                                  props: {
                                    filterName: 'Unseated',
                                    filterValue: true,
                                  },
                                }

                          track.employees(payload)
                        }}
                      >
                        Unseated
                      </Checkbox>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style={{ height: 30 }}>
                    <div style={{ padding: 'auto 0' }}>
                      <Checkbox
                        checked={filters.valueFilter.indexOf(ValueFilter.REMOTE) >= 0}
                        onChange={() => {
                          applyFilters({
                            ...filters,
                            valueFilter:
                              filters.valueFilter.indexOf(ValueFilter.REMOTE) >= 0
                                ? without(filters.valueFilter, ValueFilter.REMOTE)
                                : [...filters.valueFilter, ValueFilter.REMOTE],
                          })

                          const payload: RootEvent =
                            filters.valueFilter.indexOf(ValueFilter.REMOTE) >= 0
                              ? {
                                  eventName: Event.CLEAR_FILTER,
                                  props: {
                                    filterName: 'Remote',
                                    filterValue: null,
                                  },
                                }
                              : {
                                  eventName: Event.FILTER,
                                  props: {
                                    filterName: 'Remote',
                                    filterValue: true,
                                  },
                                }

                          track.employees(payload)
                        }}
                      >
                        Remote
                      </Checkbox>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style={{ height: 30 }}>
                    <div style={{ padding: 'auto 0' }}>
                      <Checkbox
                        checked={filters.valueFilter.indexOf(ValueFilter.MANUAL) >= 0}
                        onChange={() => {
                          applyFilters({
                            ...filters,
                            valueFilter:
                              filters.valueFilter.indexOf(ValueFilter.MANUAL) >= 0
                                ? without(filters.valueFilter, ValueFilter.MANUAL)
                                : [...filters.valueFilter, ValueFilter.MANUAL],
                          })

                          const payload: RootEvent =
                            filters.valueFilter.indexOf(ValueFilter.MANUAL) >= 0
                              ? {
                                  eventName: Event.CLEAR_FILTER,
                                  props: {
                                    filterName: 'Manual',
                                    filterValue: null,
                                  },
                                }
                              : {
                                  eventName: Event.FILTER,
                                  props: {
                                    filterName: 'Manual',
                                    filterValue: true,
                                  },
                                }

                          track.employees(payload)
                        }}
                      >
                        Manually Added
                      </Checkbox>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <Divider />
            <div style={{ marginLeft: 24 }}>
              <table style={{ minWidth: 190 }}>
                <tbody>
                  <tr>
                    <td style={{ height: 45 }}>Location</td>
                    <td>
                      <LocationSelect
                        clearable
                        mode={LocationSelect.Mode.SINGLE_FLOOR}
                        onChange={(floor) => {
                          applyFilters({
                            ...filters,
                            filters: {
                              ...filters.filters,
                              floor,
                            },
                          })

                          const payload: RootEvent = floor
                            ? {
                                eventName: Event.FILTER,
                                props: {
                                  filterName: 'Location',
                                  filterValue: floor.id,
                                },
                              }
                            : {
                                eventName: Event.CLEAR_FILTER,
                                props: {
                                  filterName: 'Location',
                                  filterValue: null,
                                },
                              }

                          track.employees(payload)
                        }}
                        buttonProps={{ iconName: '' }}
                        value={get(filters, 'filters.floor.id', null)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ height: 45 }}>Manager</td>
                    <td>
                      <EmployeeSelect
                        clearable
                        value={filters.filters.manager}
                        onChange={(manager) => {
                          applyFilters({
                            ...filters,
                            filters: {
                              ...filters.filters,
                              manager,
                            },
                          })

                          const payload: RootEvent = manager
                            ? {
                                eventName: Event.FILTER,
                                props: {
                                  filterName: 'Manager',
                                  filterValue: manager.id,
                                },
                              }
                            : {
                                eventName: Event.CLEAR_FILTER,
                                props: {
                                  filterName: 'Manager',
                                  filterValue: null,
                                },
                              }

                          track.employees(payload)
                        }}
                      />
                    </td>
                  </tr>
                  <tr style={{ height: 45 }}>
                    <td>Department</td>
                    <td>
                      <DepartmentSelect
                        clearable
                        value={filters.keyValueFilters.department}
                        onChange={(department) => {
                          applyFilters({
                            ...filters,
                            keyValueFilters: {
                              ...filters.keyValueFilters,
                              department,
                            },
                          })

                          const payload: RootEvent = department
                            ? {
                                eventName: Event.FILTER,
                                props: {
                                  filterName: 'Department',
                                  filterValue: department.id,
                                },
                              }
                            : {
                                eventName: Event.CLEAR_FILTER,
                                props: {
                                  filterName: 'Department',
                                  filterValue: null,
                                },
                              }

                          track.employees(payload)
                        }}
                      />
                    </td>
                  </tr>
                  <tr style={{ height: 45 }}>
                    <td>Type</td>
                    <td>
                      <EmployeeTypeSelect
                        clearable
                        value={filters.filters.type}
                        onChange={(type) => {
                          applyFilters({
                            ...filters,
                            filters: {
                              ...filters.filters,
                              type,
                            },
                          })

                          const payload: RootEvent = type
                            ? {
                                eventName: Event.FILTER,
                                props: {
                                  filterName: 'Type',
                                  filterValue: type,
                                },
                              }
                            : {
                                eventName: Event.CLEAR_FILTER,
                                props: {
                                  filterName: 'Type',
                                  filterValue: null,
                                },
                              }

                          track.employees(payload)
                        }}
                      />
                    </td>
                  </tr>
                  <tr style={{ height: 45 }}>
                    <td>Role</td>
                    <td>
                      <RoleSelect
                        clearable
                        value={filters.filters.role}
                        onChange={(role) => {
                          applyFilters({
                            ...filters,
                            filters: {
                              ...filters.filters,
                              role,
                            },
                          })

                          const payload: RootEvent = role
                            ? {
                                eventName: Event.FILTER,
                                props: {
                                  filterName: 'Role',
                                  filterValue: role,
                                },
                              }
                            : {
                                eventName: Event.CLEAR_FILTER,
                                props: {
                                  filterName: 'Role',
                                  filterValue: null,
                                },
                              }

                          track.employees(payload)
                        }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Flex>
        </div>
      }
    >
      <FilterIconButton filterCount={filterCount} style={{ ...targetStyle }} />
    </Popover>
  )
}
